import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmInvestmentFg, BmItemForm } from "../../pojo/bm-form";
import { BmCalcType } from "../../pojo/bm-enums";
import { BmCalcHelperService } from "../general/bm-calc-helper.service";
import { BudgetModelInvestment } from "../../pojo/bm-classes";

/**
 * INVESTMENT SERVICE
 *
 * User can change only investmentValue or year values.
 *
 * If user change PCNT row => VALUES row in corresponding year will be reset and recalculated
 * If user change VALUE row => PCNT row in corresponding year will be reset and recalculated
 * If user change investmentValue => in corresponding year => PCNT MANUAL  => YEAR VALUE = PCNT * investmentValue; QUARTERS = YEAR VALUE / 4
 *                                                            VALUE MANUAL => YEAR PCNT = VALUE / investmentValue; QUARTERS = YEAR VALUE / 4
 *
 * In each year either YEAR PCNT or YEAR VALUE could be MANUAL. If one manual => second will be disabled and CALCULATED
 *
 * Example:
 * investmentValue = 200
 *  (VALUES)     Q1 = 20   Q2 = 20   Q3 = 20   Q4=20   Y1 = 80  (CALCULATED )  <= Calculated as % multiplied by investmentValue
 *  (PCNT)       Q1 = 10%  Q2 = 10%  Q3 = 10%  Q4=10%  Y1 = 40% (MANUAL)       <= User set year value. Quarter values = Year / 4
 */
export class BmInvestmentFormService {

  static createForm(investmentSection: BudgetModelInvestment): FormGroup<BmInvestmentFg> {

    const itemsValueFa = new FormArray<FormGroup<BmItemForm>>(investmentSection.itemsValue.map(BmCalcHelperService.createBmItemForm));
    const sectionTotalRowValue = new FormControl<number>(BmCalcHelperService.recalculateRowTotal(itemsValueFa));

    const fg = new FormGroup<BmInvestmentFg>({
      uuid: new FormControl<string>(investmentSection.uuid),
      investmentValue: new FormControl<number>({ value: investmentSection.investmentValue, disabled: investmentSection.investmentValueCalcType !== BmCalcType.MANUAL }),
      investmentValueCalcType: new FormControl<BmCalcType>(investmentSection.investmentValueCalcType),
      itemsPcnt: new FormArray<FormGroup<BmItemForm>>(investmentSection.itemsPcnt.map(BmCalcHelperService.createBmItemForm)),
      itemsPcntSum: new FormControl<number>(0),
      itemsValue: itemsValueFa,
      totalRow: sectionTotalRowValue,
      totalRowPcnt: new FormControl<number>(0),
      showSection: new FormControl<boolean>(true),
    });

    BmCalcHelperService.disableItems(fg.controls.itemsValue);
    BmCalcHelperService.disableItems(fg.controls.itemsPcnt);

    return fg;
  }

  static recalculateInvestment(fg: FormGroup<BmInvestmentFg>): void {
    const invValue = fg.controls.investmentValue.getRawValue() ?? 0;
    const pcntItemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.itemsPcnt);
    const valueItemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.itemsValue);

    pcntItemsByYear.forEach(({ yearFG, quartersFG }) => {
      const valueYear = valueItemsByYear.get(yearFG.getRawValue().period.yearNumber).yearFG;
      const valueQuarters = valueItemsByYear.get(yearFG.getRawValue().period.yearNumber).quartersFG;

      if (valueYear.getRawValue().calcType !== BmCalcType.MANUAL) {
        BmCalcHelperService.patchAndDisable(valueYear, (yearFG.getRawValue().itemValue / 100) * invValue, BmCalcType.CALCULATED);

        valueQuarters.forEach((quarter) => {
          BmCalcHelperService.patchAndDisable(quarter, ((yearFG.getRawValue().itemValue / 100) * invValue) / 4, BmCalcType.CALCULATED);
        });
      } else {
        BmCalcHelperService.patchAndDisable(yearFG, invValue ? ((valueYear.getRawValue().itemValue / invValue) * 100) : 0, BmCalcType.CALCULATED);

        quartersFG.forEach((quarter) => {
          BmCalcHelperService.patchAndDisable(quarter, invValue ? ((valueYear.getRawValue().itemValue / invValue) * 100 / 4) : 0, BmCalcType.CALCULATED);
        });
      }
    });

    this.calculatePcntSum(fg);
    this.calculateTotalRow(fg);
  }

  static handleYearPcntChange(fg: FormGroup<BmInvestmentFg>): void {
    const invValue = fg.controls.investmentValue.getRawValue() ?? 0;
    const pcntItemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.itemsPcnt);
    const valueItemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.itemsValue);

    pcntItemsByYear.forEach(({ yearFG, quartersFG }) => {
      // MANUAL check is needed to prevent bug => You change Year 2 and other years will be disabled
      // GENERATED check is needed to make sure, that QUARTER data will be recalculated, after 'x' button in Year Input was clicked
      if (yearFG.getRawValue().calcType === BmCalcType.MANUAL || yearFG.getRawValue().calcType === BmCalcType.GENERATED) {
        const valueMap = valueItemsByYear.get(yearFG.getRawValue().period.yearNumber);
        BmCalcHelperService.patchAndDisable(valueMap.yearFG, (yearFG.getRawValue().itemValue / 100) * invValue, BmCalcType.CALCULATED);

        valueMap.quartersFG.forEach((quarter) => {
          BmCalcHelperService.patchAndDisable(quarter, ((yearFG.getRawValue().itemValue / 100) * invValue) / 4, BmCalcType.CALCULATED);
        });

        quartersFG.forEach((quarter) => {
          BmCalcHelperService.patchAndDisable(quarter, (yearFG.getRawValue().itemValue) / 4, BmCalcType.CALCULATED);
        });
      }
    });
    this.calculatePcntSum(fg);
    this.calculateTotalRow(fg);
  }

  static handleYearValueChange(fg: FormGroup<BmInvestmentFg>): void {
    const invValue = fg.controls.investmentValue.getRawValue() ?? 0;

    const pcntItemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.itemsPcnt);
    const valueItemsByYear = BmCalcHelperService.groupItemsByYear(fg.controls.itemsValue);

    valueItemsByYear.forEach(({ yearFG, quartersFG }) => {
      // MANUAL check is needed to prevent bug => You change Year 2 and other years will be disabled
      // GENERATED check is needed to make sure, that QUARTER data will be recalculated, after 'x' button in Year Input was clicked
      if (yearFG.getRawValue().calcType === BmCalcType.MANUAL || yearFG.getRawValue().calcType === BmCalcType.GENERATED) {
        const pcntMap = pcntItemsByYear.get(yearFG.getRawValue().period.yearNumber);
        BmCalcHelperService.patchAndDisable(pcntMap.yearFG, invValue ? (yearFG.getRawValue().itemValue / invValue) * 100 : 0, BmCalcType.CALCULATED);

        pcntMap.quartersFG.forEach((quarter) => {
          BmCalcHelperService.patchAndDisable(quarter, invValue ? (yearFG.getRawValue().itemValue / invValue) * 100 / 4 : 0, BmCalcType.CALCULATED);
        });

        quartersFG.forEach((quarter) => {
          BmCalcHelperService.patchAndDisable(quarter, (yearFG.getRawValue().itemValue) / 4, BmCalcType.CALCULATED);
        });
      }
    });
    this.calculatePcntSum(fg);
    this.calculateTotalRow(fg);
  }

  private static calculateTotalRow(fg: FormGroup<BmInvestmentFg>): void {
    fg.controls.totalRow.patchValue(BmCalcHelperService.recalculateRowTotal(fg.controls.itemsValue), { emitEvent: false });
  }

  /**
   * Calculate sum of all pcnt values in the investment section
   * @param fg investment section form group
   */
  private static calculatePcntSum(fg: FormGroup<BmInvestmentFg>): void {
    fg.controls.itemsPcntSum.patchValue(BmCalcHelperService.recalculateRowTotal(fg.controls.itemsPcnt), { emitEvent: false });
  }

}
